// TKOS Lead-Generation Wizard — entry point.
//
// The wizard runs as a single-page bundle and is embedded on retailer
// websites via `wizard-embed.js`, which loads it inside an iframe pointed at
// `/tool?retailer=RID`. With no retailer ID (e.g. direct visits to /tool),
// we fall back to a neutral default so the page still demos sensibly.

import { mount } from "./wizard";
import { initAnalytics } from "./wizard/analytics";
import "./style.css";

const root = document.getElementById("app");
if (!root) throw new Error("Missing #app root");

function readRetailerId(): string {
  try {
    const qs = new URLSearchParams(window.location.search);
    const rid = qs.get("retailer") ?? qs.get("retailerId");
    if (rid && rid.trim() !== "") return rid.trim();
  } catch {
    /* ignore */
  }
  // Pretty per-retailer link: /start/<retailerId> (shared from the portal's
  // Wizard branding settings). Pre-loads that retailer's branding.
  try {
    const pm = window.location.pathname.match(/\/start\/([^/?#]+)/);
    if (pm && pm[1]) {
      const seg = decodeURIComponent(pm[1]).trim();
      if (seg) return seg;
    }
  } catch {
    /* ignore */
  }
  // Hash fragment fallback (#retailer=foo).
  const m = window.location.hash.match(/[?&#]retailer=([^&]+)/);
  if (m && m[1]) {
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }
  // Default to the canonical single-tenant retailer. The old "demo" default
  // had no `data/retailers/demo.json`, so POST /api/public/wizard-submit
  // resolved no retailer and returned 404 (Bug 13). This id has a real
  // retailer record + branding, so a paramless /wizard/ submits successfully.
  return "r-h190uc1rtw5c";
}

const retailerId = readRetailerId();
initAnalytics(retailerId);

// Website-chat handoff: ?prefill=<token> → fetch the collected contact details
// (one-time, opaque token; no PII in the URL) and seed the wizard. Best-effort —
// a missing/expired token just starts the wizard empty.
async function boot(): Promise<void> {
  let prefill: Record<string, string> | undefined;
  try {
    const tok = new URLSearchParams(window.location.search).get("prefill");
    if (tok) {
      const r = await fetch(`/api/public/embed/prefill/${encodeURIComponent(tok)}`);
      if (r.ok) {
        const j = (await r.json()) as { ok?: boolean; data?: Record<string, string> };
        if (j && j.ok && j.data) prefill = j.data;
      }
    }
  } catch { /* ignore — start empty */ }
  await mount(root as HTMLElement, retailerId, prefill);
}
void boot();
